import { useSelector } from 'react-redux';
import { selectPermissions } from '../store/selectors/commonSelectors';

/**
 * Custom hook to check if user has required permissions
 * @param {string|string[]} requiredPermissions - Single permission string or array of permission strings
 * @param {Array} [userPermissions] - Optional user permissions array. If not provided, will fetch from Redux store
 * @returns {boolean} True if user has at least one of the required permissions
 * 
 */
export const useHasPermission = (requiredPermissions, userPermissions) => {
    // Always call useSelector unconditionally (React Hook rules)
    const reduxPermissions = useSelector(selectPermissions);
    
    // Use provided userPermissions or fall back to Redux permissions
    const permissions = userPermissions || reduxPermissions;
    
    // Ensure requiredPermissions is an array
    if (!Array.isArray(requiredPermissions)) {
        requiredPermissions = [requiredPermissions];
    }
  
    // Return true if the user has at least one of the required permissions
    return requiredPermissions.some(permission => permissions.includes(permission));
};

/**
 * Helper function to check if user has required permissions (non-hook version)
 * Useful for filtering arrays outside of React components
 * @param {string|string[]} requiredPermissions - Single permission string or array of permission strings
 * @param {Array} userPermissions - User permissions array
 * @returns {boolean} True if user has at least one of the required permissions
 */
export const hasPermission = (requiredPermissions, userPermissions) => {
    if (!userPermissions || !Array.isArray(userPermissions)) {
        return false;
    }
    
    // Ensure requiredPermissions is an array
    if (!Array.isArray(requiredPermissions)) {
        requiredPermissions = [requiredPermissions];
    }
    
    // If no permissions required, allow access
    if (requiredPermissions.length === 0) {
        return true;
    }
  
    // Return true if the user has at least one of the required permissions
    return requiredPermissions.some(permission => userPermissions.includes(permission));
};

